// src/contexts/CurrencyContext.js - Global Currency Management
import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';

// Base investment ranges in USD (will be converted for other currencies)
const BASE_RANGES = {
  monthly: [
    { usdValue: 500, label: 'Under' },
    { usdValue: 500, usdValueMax: 1000, label: '' },
    { usdValue: 1000, usdValueMax: 2500, label: '' },
    { usdValue: 2500, usdValueMax: 5000, label: '' },
    { usdValue: 5000, label: 'Over' }
  ],
  portfolio: [
    { usdValue: 0, label: 'Just starting', isSpecial: true },
    { usdValue: 10000, label: 'Under' },
    { usdValue: 10000, usdValueMax: 50000, label: '' },
    { usdValue: 50000, usdValueMax: 100000, label: '' },
    { usdValue: 100000, usdValueMax: 500000, label: '' },
    { usdValue: 500000, label: 'Over' }
  ]
};

// Currency configurations with conversion rate
export const CURRENCIES = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    locale: 'en-US',
    conversionRate: 1, // Base currency
    tickerSuffixes: ['.US', '']  // No suffix for US stocks
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    locale: 'en-IN',
    conversionRate: 83, // 1 USD = 83 INR (approximate)
    tickerSuffixes: ['.NS', '.BO']  // NSE and BSE suffixes
  }
};

// Function to generate investment ranges for a currency
const generateInvestmentRanges = (currency) => {
  const currencyInfo = CURRENCIES[currency];
  const { symbol, conversionRate } = currencyInfo;
  
  const convertAmount = (usdAmount) => {
    if (currency === 'USD') return usdAmount;
    
    // Convert to INR and round to nice numbers
    const inrAmount = usdAmount * conversionRate;
    
    // Round to nice numbers for INR
    if (inrAmount <= 1000) return Math.round(inrAmount / 100) * 100; // Round to hundreds
    if (inrAmount <= 10000) return Math.round(inrAmount / 1000) * 1000; // Round to thousands
    if (inrAmount <= 100000) return Math.round(inrAmount / 5000) * 5000; // Round to 5k
    if (inrAmount <= 1000000) return Math.round(inrAmount / 10000) * 10000; // Round to 10k
    return Math.round(inrAmount / 50000) * 50000; // Round to 50k for large amounts
  };

  const formatAmount = (amount) => {
    if (currency === 'INR') {
      // Use Indian number formatting
      if (amount >= 10000000) return `${symbol}${(amount / 10000000).toFixed(0)} Cr`;
      if (amount >= 100000) return `${symbol}${(amount / 100000).toFixed(0)} L`;
      return `${symbol}${amount.toLocaleString('en-IN')}`;
    }
    return `${symbol}${amount.toLocaleString()}`;
  };

  return {
    monthly: BASE_RANGES.monthly.map((range, index) => {
      if (range.label === 'Under') {
        const amount = convertAmount(range.usdValue);
        return {
          value: `Under ${formatAmount(amount)}`,
          display: `Under ${formatAmount(amount)}`
        };
      } else if (range.label === 'Over') {
        const amount = convertAmount(range.usdValue);
        return {
          value: `${formatAmount(amount)}+`,
          display: `${formatAmount(amount)}+`
        };
      } else {
        const minAmount = convertAmount(range.usdValue);
        const maxAmount = convertAmount(range.usdValueMax);
        return {
          value: `${formatAmount(minAmount)} - ${formatAmount(maxAmount)}`,
          display: `${formatAmount(minAmount)} - ${formatAmount(maxAmount)}`
        };
      }
    }),
    portfolio: BASE_RANGES.portfolio.map((range, index) => {
      if (range.isSpecial) {
        return {
          value: range.label,
          display: range.label
        };
      } else if (range.label === 'Under') {
        const amount = convertAmount(range.usdValue);
        return {
          value: `Under ${formatAmount(amount)}`,
          display: `Under ${formatAmount(amount)}`
        };
      } else if (range.label === 'Over') {
        const amount = convertAmount(range.usdValue);
        return {
          value: `${formatAmount(amount)}+`,
          display: `${formatAmount(amount)}+`
        };
      } else {
        const minAmount = convertAmount(range.usdValue);
        const maxAmount = convertAmount(range.usdValueMax);
        return {
          value: `${formatAmount(minAmount)} - ${formatAmount(maxAmount)}`,
          display: `${formatAmount(minAmount)} - ${formatAmount(maxAmount)}`
        };
      }
    })
  };
};

// Remove auto-detection - users will manually select currency
// This prevents breaking backend APIs that expect clean ticker symbols

// Currency formatting utility
export const formatCurrency = (amount, currencyCode, options = {}) => {
  const currency = CURRENCIES[currencyCode] || CURRENCIES.USD;
  
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${currency.symbol}0`;
  }
  
  try {
    // Use Intl.NumberFormat for proper localization
    const formatter = new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: options.minimumFractionDigits ?? 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      ...options
    });
    
    return formatter.format(amount);
  } catch (error) {
    // Fallback to simple formatting
    return `${currency.symbol}${amount.toLocaleString()}`;
  }
};

// Create Currency Context
const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  
  // Load currency from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('user_currency');
    if (savedCurrency && CURRENCIES[savedCurrency]) {
      setCurrency(savedCurrency);
    }
  }, []);
  
  // Save currency to localStorage when changed
  const updateCurrency = (newCurrency) => {
    if (CURRENCIES[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem('user_currency', newCurrency);
      logger.log(`💱 Currency changed to ${newCurrency}`);
    }
  };
  
  // Remove auto-detection - users manually select currency
  
  const value = {
    currency,
    currencyData: {
      ...CURRENCIES[currency],
      investmentRanges: generateInvestmentRanges(currency)
    },
    setCurrency: updateCurrency,
    formatCurrency: (amount, options) => formatCurrency(amount, currency, options),
    availableCurrencies: CURRENCIES
  };
  
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook to use currency context
export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};